import { denormalize } from 'normalizr'

import {
  showcaseSectionsSchema,
  showcaseSectionSchema,
} from 'client/bookmate/reducers/schemas/showcase-schemas'

import {
  getShowcaseTag,
  getSectionId,
} from 'client/bookmate/helpers/showcase-helpers'

import {
  DEFAULT_FROM_VALUE,
  DEFAULT_LIMIT_VALUE,
} from 'client/bookmate/reducers/showcase-reducer'

import { State } from 'shared/types/redux'
import { Locale } from 'client/shared/types/current-user'
import { ShowcaseAttributes } from 'client/shared/types/showcase'

export function getShowcaseAttributes(
  state: State,
  currentSlug: string,
): ShowcaseAttributes | Record<string, any> {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return {}

  return {
    language: currentShowcase.language as Locale,
    slug: currentShowcase.slug,
    contentType: currentShowcase.contentType,
  }
}

export function getRootNavigation(state: State) {
  return state.showcase.rootNavigation
}

export function getShowcaseSections(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase || !currentShowcase.sections)
    return {
      items: [],
      loading: false,
    }

  const { items, loading } = currentShowcase.sections

  return {
    items: denormalize(items, showcaseSectionsSchema, state.entities) || [],
    loading: loading && items?.length === 0,
  }
}

export function getShowcaseNavigation(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase)
    return {
      items: [],
      loading: false,
    }

  const { items, loading } = currentShowcase.navigation

  return {
    items,
    loading: loading && items?.length === 0,
  }
}

export function getShowcaseUuid(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return null

  return currentShowcase.uuid
}

export function getShowcaseTitle(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return null

  return currentShowcase.title
}

export function getShowcaseTopics(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return null

  return currentShowcase.hasTopics
}

export function getShowcaseBackground(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return null

  return currentShowcase.background
}

export function getShowcaseHierarchy(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return []

  return currentShowcase.hierarchy
}

export function getShowcaseSectionBySlug(
  state: State,
  currentSlug: string,
  slug: string,
) {
  const attributes = getShowcaseAttributes(state, currentSlug)
  if (!attributes) return
  const showcaseTag = getShowcaseTag(attributes)
  const sectionId = getSectionId(slug, showcaseTag)

  return denormalize(sectionId, showcaseSectionSchema, state.entities) || null
}

export function getShowcaseFrom(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return DEFAULT_FROM_VALUE

  return currentShowcase.loadSectionsFrom
}

export function getShowcaseLimit(state: State, currentSlug: string) {
  const currentShowcase = state.showcase.content[currentSlug]

  if (!currentSlug || !currentShowcase) return DEFAULT_LIMIT_VALUE

  return currentShowcase.limit
}
